import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { MapView } from "@/components/MapView";
import { get, RENTAL_OPTIONS } from "@/lib/api";

const sel = "rounded-full border border-[#CBD5E1] bg-white px-4 py-2 text-sm";

export default function MapPage() {
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(["state", "pets", "rental", "max_fee"].filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
  const { data: states = [] } = useQuery({ queryKey: ["states"], queryFn: () => get("/states") });
  const { data: points = [], isLoading } = useQuery({ queryKey: ["map", filters], queryFn: () => get("/communities/map", filters) });
  const set = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };

  return (
    <div data-testid="map-page" className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Seo title="HOA Map View" description="Explore HOA communities across the United States on an interactive map." path="/map" />
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow">Map view</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2150]">HOAs on the map</h1>
          <p className="mt-2 text-slate-600 text-sm" aria-live="polite" data-testid="map-count">{isLoading ? "Loading communities…" : `${points.length.toLocaleString()} communities shown. Click a pin for details.`}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="sr-only" htmlFor="map-state">State</label>
          <select id="map-state" className={sel} value={filters.state || ""} onChange={(e) => set("state", e.target.value)} data-testid="map-filter-state">
            <option value="">All states</option>{states.map((s) => <option key={s.slug} value={s.slug}>{s.state}</option>)}
          </select>
          <label className="sr-only" htmlFor="map-pets">Pets</label>
          <select id="map-pets" className={sel} value={filters.pets || ""} onChange={(e) => set("pets", e.target.value)} data-testid="map-filter-pets">
            <option value="">Any pet policy</option><option value="yes">Pets allowed</option><option value="no">No pets</option>
          </select>
          <label className="sr-only" htmlFor="map-rental">Rentals</label>
          <select id="map-rental" className={sel} value={filters.rental || ""} onChange={(e) => set("rental", e.target.value)} data-testid="map-filter-rental">
            <option value="">Any rental policy</option>{RENTAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label className="sr-only" htmlFor="map-fee">Max fee</label>
          <select id="map-fee" className={sel} value={filters.max_fee || ""} onChange={(e) => set("max_fee", e.target.value)} data-testid="map-filter-fee">
            <option value="">Any fee</option>{[100, 200, 300, 500].map((v) => <option key={v} value={v}>Under ${v}/mo</option>)}
          </select>
        </div>
      </div>
      <div className="h-[70vh] min-h-[480px]"><MapView points={points} /></div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600" aria-label="Map legend">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#0B2150]" />Directory listing</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#4D8A2A]" />Researched listing</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#03A896]" />Verified</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[#2563EB]" />Pending verification</span>
      </div>
    </div>
  );
}
